import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useResumeRefresh } from '../hooks/useResumeRefresh';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ArrowUpRight, Plus, Search, Edit2, Trash2, Mail, Phone, FileText, DollarSign, Building2, CheckCircle2, Users, Download, Send, ChevronDown, ChevronUp, Calendar, CreditCard, Landmark, Copy, ExternalLink, AlertCircle, Link2, User, Settings, LogOut, Menu, X, Shield, Upload, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { toast } from 'sonner';
import { apiService } from '@/lib/api';
import {formatDisplayDate, formatDate} from '@/lib/dateUtils';

// Plaid Link is disabled in preview environment due to script conflicts
// Users can still manage contractors without bank connection features
const usePlaidLink = () => ({ open: () => toast.info('Bank connection is available after deployment'), ready: false });

function GlobalContractors({ user, onLogout }) {
  const navigate = useNavigate();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [contractors, setContractors] = useState([]);
  const [payments, setPayments] = useState([]);
  const [projects, setProjects] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isPaymentDialogOpen, setIsPaymentDialogOpen] = useState(false);
  const [is1099DialogOpen, setIs1099DialogOpen] = useState(false);
  const [selectedContractor, setSelectedContractor] = useState(null);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear().toString());
  const [expandedContractors, setExpandedContractors] = useState({});  // Track which contractors have expanded payment history
  
  // ACH Payment states
  const [isACHPaymentDialogOpen, setIsACHPaymentDialogOpen] = useState(false);
  const [isBankSetupDialogOpen, setIsBankSetupDialogOpen] = useState(false);
  const [achPaymentAmount, setAchPaymentAmount] = useState('');
  const [achPaymentDescription, setAchPaymentDescription] = useState('');
  const [bankSetupUrl, setBankSetupUrl] = useState('');
  const [contractorBankStatus, setContractorBankStatus] = useState({});
  
  // v2.4: Compliance health helper — returns a pill descriptor based on
  // W-9 + COI state. Highest-severity issue wins (expired > missing >
  // expires_soon). Returns null if everything is healthy.
  const getComplianceHealth = (c) => {
    if (!c) return null;
    const w9Missing = !c.w9_received;
    const coiMissing = !c.coi_received;

    // Compute days until COI expires + expired flag (works even when backend
    // doesn't set c.coi_expired on the response).
    let daysUntilCOI = null;
    let coiExpired = !!c.coi_expired;
    if (c.coi_expiration) {
      const exp = new Date(c.coi_expiration);
      if (!isNaN(exp)) {
        daysUntilCOI = Math.ceil((exp - new Date()) / (1000 * 60 * 60 * 24));
        if (daysUntilCOI < 0) coiExpired = true;
      }
    }

    if (coiExpired) {
      return { label: '⚠ COI expired', bg: 'bg-red-50', text: 'text-red-800', border: 'border-red-300' };
    }
    if (coiMissing && w9Missing) {
      return { label: '⚠ W-9 + COI missing', bg: 'bg-red-50', text: 'text-red-800', border: 'border-red-300' };
    }
    if (coiMissing) {
      return { label: '⚠ COI missing', bg: 'bg-red-50', text: 'text-red-800', border: 'border-red-300' };
    }
    if (w9Missing) {
      return { label: '⚠ W-9 missing', bg: 'bg-amber-50', text: 'text-amber-800', border: 'border-amber-300' };
    }
    if (daysUntilCOI != null && daysUntilCOI <= 30 && daysUntilCOI >= 0) {
      return { label: `COI expires in ${daysUntilCOI}d`, bg: 'bg-amber-50', text: 'text-amber-800', border: 'border-amber-300' };
    }
    if (daysUntilCOI != null && daysUntilCOI > 30) {
      return { label: '✓ Compliant', bg: 'bg-emerald-50', text: 'text-emerald-800', border: 'border-emerald-300' };
    }
    return null;
  };

  // W9/COI Document Upload states
  const [isDocumentUploadOpen, setIsDocumentUploadOpen] = useState(false);
  const [documentType, setDocumentType] = useState('w9'); // 'w9' or 'coi'
  const [documentFile, setDocumentFile] = useState(null);
  const [coiExpiration, setCoiExpiration] = useState('');
  const [isUploadingDocument, setIsUploadingDocument] = useState(false);
  
  // User's own bank account connection (Plaid)
  const [userBankAccount, setUserBankAccount] = useState(null);
  const [plaidLinkToken, setPlaidLinkToken] = useState(null);
  const [isLoadingBankStatus, setIsLoadingBankStatus] = useState(true);
  
  const [newContractor, setNewContractor] = useState({
    project_id: '',
    name: '',
    company: '',
    email: '',
    phone: '',
    street_address: '',
    city: '',
    state: '',
    zip: '',
    ein_ssn: ''
  });
  
  const [newPayment, setNewPayment] = useState({
    amount: '',
    payment_date: new Date().toISOString().split('T')[0],
    notes: '',
    payment_method: 'check'
  });

  // Toggle payment history visibility for a contractor
  const togglePaymentHistory = (contractorId) => {
    setExpandedContractors(prev => ({
      ...prev,
      [contractorId]: !prev[contractorId]
    }));
  };

  // Load user's bank account status
  const loadUserBankAccount = useCallback(async () => {
    if (!user?.id) return;
    try {
      setIsLoadingBankStatus(true);
      const response = await apiService.getUserBankAccount(user.id);
      setUserBankAccount(response.data.connected ? response.data : null);
    } catch (error) {
      console.error('Failed to load bank account:', error);
    } finally {
      setIsLoadingBankStatus(false);
    }
  }, [user?.id]);

  // Initialize Plaid Link token
  const initPlaidLink = useCallback(async () => {
    if (!user?.id) return;
    try {
      const response = await apiService.createPlaidLinkToken(user.id);
      setPlaidLinkToken(response.data.link_token);
    } catch (error) {
      // Silently fail - Plaid connection is optional and errors here shouldn't disrupt the user
      console.error('Failed to create Plaid link token:', error);
    }
  }, [user?.id]);

  // Handle successful Plaid Link connection
  const onPlaidSuccess = useCallback(async (publicToken, metadata) => {
    try {
      toast.loading('Connecting your bank account...');
      
      const accountData = metadata.accounts[0];
      const response = await apiService.exchangePlaidToken({
        user_id: user.id,
        public_token: publicToken,
        account_id: accountData.id,
        institution_name: metadata.institution?.name || 'Bank',
        account_name: accountData.name || 'Account',
        account_mask: accountData.mask || '****'
      });
      
      toast.dismiss();
      toast.success(response.data.message || 'Bank account connected!');
      
      // Reload bank account status
      await loadUserBankAccount();
      
    } catch (error) {
      toast.dismiss();
      console.error('Failed to connect bank account:', error);
      toast.error(error.response?.data?.detail || 'Failed to connect bank account');
    }
  }, [user?.id, loadUserBankAccount]);

  // Plaid Link hook
  const { open: openPlaidLink, ready: plaidReady } = usePlaidLink({
    token: plaidLinkToken,
    onSuccess: onPlaidSuccess,
    onExit: (err) => {
      if (err) console.error('Plaid Link exit error:', err);
    }
  });

  useEffect(() => {
    loadData();
    loadUserBankAccount();
    initPlaidLink();
  }, [loadUserBankAccount, initPlaidLink]);
  useResumeRefresh(loadData);

  const loadData = async () => {
    try {
      setIsLoading(true);
      // Load projects first
      const projectsRes = await apiService.getProjects(user.id);
      setProjects(projectsRes.data || []);
      
      // Load contractors using user_id
      const contractorsRes = await apiService.getContractorsByUser(user.id);
      setContractors(contractorsRes.data || []);
      
      // Load payments
      await loadPayments();
    } catch (error) {
      console.error('Failed to load data:', error);
      toast.error('Failed to load contractors');
    } finally {
      setIsLoading(false);
    }
  };

  const loadContractors = async () => {
    try {
      setIsLoading(true);
      const response = await apiService.getContractorsByUser(user.id);
      setContractors(response.data || []);
    } catch (error) {
      console.error('Failed to load contractors:', error);
      toast.error('Failed to load contractors');
    } finally {
      setIsLoading(false);
    }
  };

  const loadPayments = async () => {
    try {
      const response = await apiService.getContractorPaymentsByUser(user.id);
      setPayments(response.data || []);
    } catch (error) {
      console.error('Failed to load payments:', error);
    }
  };

  const handleCreateContractor = async (e) => {
    e.preventDefault();
    if (!newContractor.company || !newContractor.email) {
      toast.error('Company name and email are required');
      return;
    }
    try {
      await apiService.createContractor({
        project_id: newContractor.project_id || null,  // Optional - can be null for global contractors
        user_id: user.id,  // Associate contractor with the user
        name: newContractor.name || null,
        email: newContractor.email,
        phone: newContractor.phone || null,
        company: newContractor.company,
        street_address: newContractor.street_address || null,
        city: newContractor.city || null,
        state: newContractor.state || null,
        zip: newContractor.zip || null,
        ein_ssn: newContractor.ein_ssn || null
      });
      toast.success('Contractor added successfully!');
      setIsAddDialogOpen(false);
      setNewContractor({
        project_id: '',
        name: '',
        company: '',
        email: '',
        phone: '',
        street_address: '',
        city: '',
        state: '',
        zip: '',
        ein_ssn: ''
      });
      loadData();
    } catch (error) {
      console.error('Failed to create contractor:', error);
      toast.error('Failed to add contractor');
    }
  };

  const handleUpdateContractor = async (e) => {
    e.preventDefault();
    try {
      await apiService.updateContractor(selectedContractor.id, selectedContractor);
      toast.success('Contractor updated successfully!');
      setIsEditDialogOpen(false);
      setSelectedContractor(null);
      loadContractors();
    } catch (error) {
      console.error('Failed to update contractor:', error);
      toast.error('Failed to update contractor');
    }
  };

  const handleDeleteContractor = async (contractorId) => {
    if (!window.confirm('Are you sure you want to delete this contractor?')) return;
    try {
      await apiService.deleteContractor(contractorId);
      toast.success('Contractor deleted');
      loadContractors();
    } catch (error) {
      console.error('Failed to delete contractor:', error);
      toast.error('Failed to delete contractor');
    }
  };

  const handleAddPayment = async (e) => {
    e.preventDefault();
    try {
      await apiService.createContractorPayment({
        user_id: user.id,
        contractor_id: selectedContractor.id,
        amount: parseFloat(newPayment.amount),
        payment_date: newPayment.payment_date,
        notes: newPayment.notes,
        payment_method: newPayment.payment_method
      });
      toast.success('Payment recorded!');
      setIsPaymentDialogOpen(false);
      setNewPayment({
        amount: '',
        payment_date: new Date().toISOString().split('T')[0],
        notes: '',
        payment_method: 'check'
      });
      loadPayments();
    } catch (error) {
      console.error('Failed to add payment:', error);
      toast.error('Failed to record payment');
    }
  };

  // ACH Payment Handlers
  const handleSetupBankAccount = async (contractor) => {
    try {
      toast.loading('Setting up bank account link...');
      const response = await apiService.setupContractorBankAccount(contractor.id);
      toast.dismiss();
      
      setBankSetupUrl(response.data.onboarding_url);
      setSelectedContractor(contractor);
      setIsBankSetupDialogOpen(true);
    } catch (error) {
      toast.dismiss();
      console.error('Failed to setup bank account:', error);
      toast.error(error.response?.data?.detail || 'Failed to create bank setup link');
    }
  };

  const handleCheckBankStatus = async (contractorId) => {
    try {
      const response = await apiService.getContractorBankStatus(contractorId);
      setContractorBankStatus(prev => ({
        ...prev,
        [contractorId]: response.data
      }));
      return response.data;
    } catch (error) {
      console.error('Failed to check bank status:', error);
      return null;
    }
  };

  const handleOpenACHPayment = async (contractor) => {
    // First check bank status
    const status = await handleCheckBankStatus(contractor.id);
    
    if (!status?.can_receive_payments) {
      toast.error(`${contractor.company || contractor.name} hasn't set up their bank account yet. Please send them a setup link first.`);
      return;
    }
    
    setSelectedContractor(contractor);
    setAchPaymentAmount('');
    setAchPaymentDescription('');
    setIsACHPaymentDialogOpen(true);
  };

  const handleSendACHPayment = async (e) => {
    e.preventDefault();
    
    if (!achPaymentAmount || parseFloat(achPaymentAmount) <= 0) {
      toast.error('Please enter a valid payment amount');
      return;
    }
    
    try {
      toast.loading('Initiating ACH transfer...');
      
      const response = await apiService.sendACHPayment({
        contractor_id: selectedContractor.id,
        amount: parseFloat(achPaymentAmount),
        description: achPaymentDescription || `Payment to ${selectedContractor.name}`
      }, user.id);
      
      toast.dismiss();
      toast.success(response.data.message);
      
      setIsACHPaymentDialogOpen(false);
      setAchPaymentAmount('');
      setAchPaymentDescription('');
      
      // Reload payments to show the new payment
      loadPayments();
      
    } catch (error) {
      toast.dismiss();
      console.error('Failed to send ACH payment:', error);
      toast.error(error.response?.data?.detail || 'Failed to send payment');
    }
  };

  const copyBankSetupLink = () => {
    navigator.clipboard.writeText(bankSetupUrl);
    toast.success('Link copied to clipboard!');
  };

  // Handle W9/COI Document Upload
  const handleDocumentUpload = async () => {
    if (!documentFile || !selectedContractor) {
      toast.error('Please select a file to upload');
      return;
    }

    setIsUploadingDocument(true);
    try {
      const formData = new FormData();
      formData.append('file', documentFile);
      
      if (documentType === 'w9') {
        await apiService.uploadContractorW9(selectedContractor.id, formData);
        toast.success('W-9 uploaded successfully!');
      } else if (documentType === 'coi') {
        // v2.4: COI expiration is MANDATORY (backend enforces too)
        if (!coiExpiration) {
          toast.error('Please enter the COI expiration date before uploading.');
          setIsUploadingDocument(false);
          return;
        }
        formData.append('expiration_date', coiExpiration);
        await apiService.uploadContractorCOI(selectedContractor.id, formData);
        toast.success('Certificate of Insurance uploaded successfully!');
      }
      
      // Reset and close
      setDocumentFile(null);
      setCoiExpiration('');
      setIsDocumentUploadOpen(false);
      
      // Reload contractors to show updated status
      loadContractors();
    } catch (error) {
      console.error('Document upload error:', error);
      toast.error(error.response?.data?.detail || `Failed to upload ${documentType === 'w9' ? 'W-9' : 'COI'}`);
    } finally {
      setIsUploadingDocument(false);
    }
  };

  // Open document upload dialog
  const openDocumentUpload = (contractor, type) => {
    setSelectedContractor(contractor);
    setDocumentType(type);
    setDocumentFile(null);
    setCoiExpiration('');
    setIsDocumentUploadOpen(true);
  };

  const getContractorPayments = (contractorId) => {
    return payments.filter(p => p.contractor_id === contractorId);
  };

  const getContractorTotalPaid = (contractorId, year = null) => {
    let contractorPayments = payments.filter(p => p.contractor_id === contractorId);
    if (year) {
      contractorPayments = contractorPayments.filter(p => 
        new Date(p.payment_date).getFullYear().toString() === year
      );
    }
    return contractorPayments.reduce((sum, p) => sum + (p.amount || 0), 0);
  };

  const generate1099 = (contractor) => {
    const year = parseInt(selectedYear);
    const totalPaid = getContractorTotalPaid(contractor.id, selectedYear);
    
    if (totalPaid < 600) {
      toast.info(`Total paid to ${contractor.company || contractor.name} in ${year} is $${totalPaid.toFixed(2)}. 1099 is only required for payments of $600 or more.`);
      return;
    }
    
    // Generate 1099 data - use business_name for payer, fallback to user name
    const data1099 = {
      tax_year: year,
      payer: {
        name: user.business_name || user.name || '',
        address: user.address || '',
        city: user.city || '',
        state: user.state || '',
        zip: user.zip_code || '',
        tin: user.tax_id || '',
        state_id: user.state_tax_id || ''
      },
      recipient: {
        name: contractor.company || contractor.name || '',
        address: contractor.street_address || '',
        city: contractor.city || '',
        state: contractor.state || '',
        zip: contractor.zip || '',
        tin: contractor.ein_ssn || ''
      },
      amount: totalPaid
    };
    
    // Check if payer info is complete
    const missingFields = [];
    if (!data1099.payer.name) missingFields.push('Business Name');
    if (!data1099.payer.tin) missingFields.push('Tax ID (EIN)');
    if (!data1099.payer.address) missingFields.push('Address');
    if (!data1099.payer.city) missingFields.push('City');
    if (!data1099.payer.state) missingFields.push('State');
    if (!data1099.payer.zip) missingFields.push('ZIP Code');
    
    if (missingFields.length > 0) {
      toast.error(`Missing Payer Info: ${missingFields.join(', ')}. Go to Account Settings to complete.`);
      return;
    }
    
    // Generate clean CSV for accountant - 1099-NEC format
    let csvContent = `1099-NEC Data for Tax Year ${year}\n`;
    csvContent += `Generated: ${formatDate(new Date())}\n\n`;
    
    csvContent += `PAYER INFORMATION\n`;
    csvContent += `Payer Name,${data1099.payer.name}\n`;
    csvContent += `Payer Address,${data1099.payer.address}\n`;
    csvContent += `Payer City,${data1099.payer.city}\n`;
    csvContent += `Payer State,${data1099.payer.state}\n`;
    csvContent += `Payer ZIP,${data1099.payer.zip}\n`;
    csvContent += `Payer TIN (EIN),${data1099.payer.tin}\n`;
    csvContent += `Payer State ID,${data1099.payer.state_id}\n\n`;
    
    csvContent += `RECIPIENT INFORMATION\n`;
    csvContent += `Recipient Name,${data1099.recipient.name}\n`;
    csvContent += `Recipient Address,${data1099.recipient.address}\n`;
    csvContent += `Recipient City,${data1099.recipient.city}\n`;
    csvContent += `Recipient State,${data1099.recipient.state}\n`;
    csvContent += `Recipient ZIP,${data1099.recipient.zip}\n`;
    csvContent += `Recipient TIN (SSN/EIN),${data1099.recipient.tin}\n\n`;
    
    csvContent += `PAYMENT INFORMATION\n`;
    csvContent += `Box 1 - Nonemployee Compensation,$${totalPaid.toFixed(2)}\n`;
    
    // Download - use company name if no contact name
    const fileName = (contractor.company || contractor.name || 'contractor').replace(/[^a-zA-Z0-9]/g, '_');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `1099_NEC_${fileName}_${year}.csv`;
    link.click();
    
    toast.success(`1099-NEC generated for ${contractor.company || contractor.name}`);
    setIs1099DialogOpen(false);
  };

  const requestForms = async (contractor) => {
    if (!contractor.email) {
      toast.error('Contractor does not have an email address');
      return;
    }
    
    try {
      await apiService.requestContractorForms(contractor.id, {
        contractor_email: contractor.email,
        contractor_name: contractor.company || contractor.name,
        requester_name: user.name || 'Property Owner',
        forms_requested: ['W9', 'COI', 'Lien Waiver']
      });
      toast.success(`Form request sent to ${contractor.email}`);
    } catch (error) {
      console.error('Failed to send form request:', error);
      toast.error('Failed to send form request');
    }
  };

  const filteredContractors = contractors.filter(c =>
    c.company?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.specialty?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white shadow-lg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 sm:py-6">
          <div className="flex items-center justify-between">
            <div className="min-w-0 flex-1">
              <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold">Contractors</h1>
              <p className="text-blue-100 mt-1 text-xs sm:text-sm hidden sm:block">Manage all contractors across projects</p>
            </div>
            
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/60" />
              <Input
                placeholder="Search contractors..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9 w-64 bg-white/10 border-white/20 text-white placeholder:text-white/60 focus:bg-white/20"
              />
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Add Contractor Button */}
        <div className="flex justify-end gap-2 mb-6">
          <Button variant="outline" onClick={() => navigate('/contractor-payments')} data-testid="open-payment-report">
            <FileText className="w-4 h-4 mr-2" />
            Payment Report
          </Button>
          <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-blue-600 hover:bg-blue-700">
                <Plus className="w-4 h-4 mr-2" />
                Add Contractor
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle>Add New Contractor</DialogTitle>
                <DialogDescription>Add a contractor to your network. You can optionally assign them to a project.</DialogDescription>
              </DialogHeader>
              <form onSubmit={handleCreateContractor} className="space-y-4">
                <div className="space-y-2">
                  <Label>Assign to Project <span className="text-slate-400 font-normal">(Optional)</span></Label>
                  <Select
                    value={newContractor.project_id || "none"}
                    onValueChange={(value) => setNewContractor({...newContractor, project_id: value === "none" ? "" : value})}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select a project (or leave blank for global)" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No Project (Global Contractor)</SelectItem>
                      {projects.map((project) => (
                        <SelectItem key={project.id} value={project.id}>
                          {project.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-slate-500">Global contractors can be assigned to phases in any project later.</p>
                </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Company Name *</Label>
                        <Input
                          required
                          value={newContractor.company}
                          onChange={(e) => setNewContractor({...newContractor, company: e.target.value})}
                          placeholder="ABC Contractors LLC"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Contact Name</Label>
                        <Input
                          value={newContractor.name}
                          onChange={(e) => setNewContractor({...newContractor, name: e.target.value})}
                          placeholder="John Smith (optional)"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Email *</Label>
                        <Input
                          type="email"
                          required
                          value={newContractor.email}
                          onChange={(e) => setNewContractor({...newContractor, email: e.target.value})}
                          placeholder="john@example.com"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Phone</Label>
                        <Input
                          value={newContractor.phone}
                          onChange={(e) => setNewContractor({...newContractor, phone: e.target.value})}
                          placeholder="(555) 123-4567"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>Street Address</Label>
                      <Input
                        value={newContractor.street_address}
                        onChange={(e) => setNewContractor({...newContractor, street_address: e.target.value})}
                        placeholder="1811 Chandler St SW"
                      />
                    </div>
                    <div className="grid grid-cols-3 gap-4">
                      <div className="space-y-2">
                        <Label>City</Label>
                        <Input
                          value={newContractor.city}
                          onChange={(e) => setNewContractor({...newContractor, city: e.target.value})}
                          placeholder="Cedar Rapids"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>State</Label>
                        <Input
                          value={newContractor.state}
                          onChange={(e) => setNewContractor({...newContractor, state: e.target.value})}
                          placeholder="IA"
                          maxLength={2}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>ZIP Code</Label>
                        <Input
                          value={newContractor.zip}
                          onChange={(e) => setNewContractor({...newContractor, zip: e.target.value})}
                          placeholder="52402"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>SSN / EIN</Label>
                      <Input
                        value={newContractor.ein_ssn}
                        onChange={(e) => setNewContractor({...newContractor, ein_ssn: e.target.value})}
                        placeholder="XXX-XX-XXXX or XX-XXXXXXX"
                      />
                      <p className="text-xs text-slate-500">Enter SSN (for individuals) or EIN (for businesses)</p>
                    </div>
                    <DialogFooter>
                      <Button type="button" variant="outline" onClick={() => setIsAddDialogOpen(false)}>
                        Cancel
                      </Button>
                      <Button type="submit" className="bg-blue-600 hover:bg-blue-700">
                        Add Contractor
                      </Button>
                    </DialogFooter>
                  </form>
                </DialogContent>
              </Dialog>
            </div>

        {/* User Bank Account Connection Card */}
        <Card className="mb-6 border-blue-200 bg-gradient-to-r from-blue-50 to-slate-50">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className={`w-12 h-12 rounded-full flex items-center justify-center ${userBankAccount ? 'bg-green-100' : 'bg-blue-100'}`}>
                  <Landmark className={`w-6 h-6 ${userBankAccount ? 'text-green-600' : 'text-blue-600'}`} />
                </div>
                <div>
                  <h3 className="font-semibold text-lg text-slate-800">Your Business Bank Account</h3>
                  {isLoadingBankStatus ? (
                    <p className="text-sm text-slate-500">Checking connection status...</p>
                  ) : userBankAccount ? (
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className="bg-green-50 text-green-700 border-green-300">
                        <CheckCircle2 className="w-3 h-3 mr-1" />
                        Connected
                      </Badge>
                      <span className="text-sm text-slate-600">
                        {userBankAccount.institution_name} ••••{userBankAccount.account_mask}
                      </span>
                    </div>
                  ) : (
                    <p className="text-sm text-slate-500">Connect your bank to send ACH payments to contractors</p>
                  )}
                </div>
              </div>
              
              <div className="flex items-center gap-3">
                {userBankAccount ? (
                  <>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => openPlaidLink()}
                      disabled={!plaidReady}
                    >
                      <Link2 className="w-4 h-4 mr-2" />
                      Change Bank
                    </Button>
                  </>
                ) : (
                  <Button 
                    className="bg-blue-600 hover:bg-blue-700"
                    onClick={() => openPlaidLink()}
                    disabled={!plaidReady}
                  >
                    <Landmark className="w-4 h-4 mr-2" />
                    Connect Bank Account
                  </Button>
                )}
              </div>
            </div>
            
            {!userBankAccount && !isLoadingBankStatus && (
              <Alert className="mt-4 bg-amber-50 border-amber-200">
                <AlertCircle className="h-4 w-4 text-amber-600" />
                <AlertDescription className="text-amber-800">
                  To send ACH payments directly to contractors, connect your business bank account first.
                </AlertDescription>
              </Alert>
            )}
          </CardContent>
        </Card>

        {isLoading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-2 text-slate-500">Loading contractors...</p>
          </div>
        ) : filteredContractors.length === 0 ? (
          <Card className="text-center py-12">
            <CardContent>
              <Users className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-slate-800 mb-2">No contractors yet</h3>
              <p className="text-slate-500 mb-4">Add contractors to manage them across all your projects</p>
              <Button onClick={() => setIsAddDialogOpen(true)} className="bg-blue-600 hover:bg-blue-700">
                <Plus className="w-4 h-4 mr-2" />
                Add Your First Contractor
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4">
            {filteredContractors.map((contractor) => {
              const totalPaid = getContractorTotalPaid(contractor.id);
              const yearPaid = getContractorTotalPaid(contractor.id, new Date().getFullYear().toString());
              
              return (
                <Card key={contractor.id} className="hover:shadow-md transition-shadow">
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-4">
                        <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                          <Users className="w-6 h-6 text-blue-600" />
                        </div>
                        <div>
                          <h3 className="font-semibold text-lg text-slate-800">{contractor.company}</h3>
                          {contractor.name && (
                            <p className="text-slate-600">{contractor.name}</p>
                          )}
                          <div className="flex flex-wrap gap-2 mt-2">
                            {contractor.specialty && (
                              <Badge variant="secondary" className="capitalize">
                                {contractor.specialty}
                              </Badge>
                            )}
                            {contractor.email && (
                              <span className="text-sm text-slate-500 flex items-center gap-1">
                                <Mail className="w-3 h-3" /> {contractor.email}
                              </span>
                            )}
                            {contractor.phone && (
                              <span className="text-sm text-slate-500 flex items-center gap-1">
                                <Phone className="w-3 h-3" /> {contractor.phone}
                              </span>
                            )}
                          </div>
                          
                          {/* W9/COI Status Badges + v2.4 Compliance Health pill */}
                          <div className="flex flex-wrap gap-2 mt-2">
                            <Badge 
                              variant="outline" 
                              className={`cursor-pointer ${contractor.w9_received ? 'bg-green-50 text-green-700 border-green-200' : 'bg-red-50 text-red-700 border-red-200'}`}
                              onClick={() => openDocumentUpload(contractor, 'w9')}
                            >
                              <FileText className="w-3 h-3 mr-1" />
                              W-9 {contractor.w9_received ? '✓' : '- Upload'}
                            </Badge>
                            <Badge 
                              variant="outline" 
                              className={`cursor-pointer ${contractor.coi_received && !contractor.coi_expired ? 'bg-green-50 text-green-700 border-green-200' : contractor.coi_expired ? 'bg-orange-50 text-orange-700 border-orange-200' : 'bg-red-50 text-red-700 border-red-200'}`}
                              onClick={() => openDocumentUpload(contractor, 'coi')}
                            >
                              <Shield className="w-3 h-3 mr-1" />
                              COI {contractor.coi_received && !contractor.coi_expired ? '✓' : contractor.coi_expired ? '- Expired' : '- Upload'}
                            </Badge>
                            {/* v2.4 Compliance Health pill */}
                            {(() => {
                              const h = getComplianceHealth(contractor);
                              if (!h) return null;
                              return (
                                <Badge variant="outline" className={`${h.bg} ${h.text} ${h.border} font-medium`}>
                                  {h.label}
                                </Badge>
                              );
                            })()}
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-2 flex-wrap">
                        <div className="text-right mr-4">
                          <p className="text-sm text-slate-500">YTD Payments</p>
                          <p className="text-lg font-semibold text-slate-800">${yearPaid.toLocaleString()}</p>
                          {totalPaid !== yearPaid && (
                            <p className="text-xs text-slate-400">Total: ${totalPaid.toLocaleString()}</p>
                          )}
                        </div>
                        
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setSelectedContractor({...contractor});
                            setIsEditDialogOpen(true);
                          }}
                        >
                          <Edit2 className="w-4 h-4 mr-1" />
                          Edit
                        </Button>
                        
                        <Button
                          size="sm"
                          className="bg-green-600 hover:bg-green-700 text-white"
                          onClick={() => {
                            setSelectedContractor(contractor);
                            setIsPaymentDialogOpen(true);
                          }}
                        >
                          <DollarSign className="w-4 h-4 mr-1" />
                          Log Payment
                        </Button>
                        
                        {/* ACH Direct Payment Button */}
                        {contractor.stripe_payouts_enabled ? (
                          <Button
                            size="sm"
                            className="bg-blue-600 hover:bg-blue-700 text-white"
                            onClick={() => handleOpenACHPayment(contractor)}
                          >
                            <Landmark className="w-4 h-4 mr-1" />
                            Send ACH
                          </Button>
                        ) : (
                          <Button
                            size="sm"
                            variant="outline"
                            className="border-blue-300 text-blue-600 hover:bg-blue-50"
                            onClick={() => handleSetupBankAccount(contractor)}
                          >
                            <Landmark className="w-4 h-4 mr-1" />
                            Setup Bank
                          </Button>
                        )}
                        
                        <Button
                          size="sm"
                          className="bg-orange-500 hover:bg-orange-600 text-white"
                          onClick={() => requestForms(contractor)}
                        >
                          <ArrowUpRight className="w-4 h-4 mr-1" />
                          Request Forms
                        </Button>
                        
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setSelectedContractor(contractor);
                            setIs1099DialogOpen(true);
                          }}
                        >
                          <FileText className="w-4 h-4 mr-1" />
                          1099
                        </Button>
                        
                        <Button
                          variant="ghost"
                          size="icon"
                          className="text-red-500 hover:text-red-700"
                          onClick={() => handleDeleteContractor(contractor.id)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                    
                    {/* Payment History Toggle */}
                    <div className="mt-4 pt-4 border-t border-slate-200">
                      <button
                        onClick={() => togglePaymentHistory(contractor.id)}
                        className="flex items-center justify-between w-full text-left text-sm font-medium text-slate-700 hover:text-slate-900"
                      >
                        <span className="flex items-center gap-2">
                          <DollarSign className="w-4 h-4" />
                          Payment History ({getContractorPayments(contractor.id).length} payments)
                        </span>
                        {expandedContractors[contractor.id] ? (
                          <ChevronUp className="w-4 h-4" />
                        ) : (
                          <ChevronDown className="w-4 h-4" />
                        )}
                      </button>
                      
                      {expandedContractors[contractor.id] && (
                        <div className="mt-3 space-y-2">
                          {getContractorPayments(contractor.id).length === 0 ? (
                            <p className="text-sm text-slate-500 italic py-2">No payments recorded yet</p>
                          ) : (
                            <div className="max-h-60 overflow-y-auto">
                              <table className="w-full text-sm">
                                <thead className="bg-slate-50 sticky top-0">
                                  <tr>
                                    <th className="text-left py-2 px-3 font-medium text-slate-600">Date</th>
                                    <th className="text-left py-2 px-3 font-medium text-slate-600">Amount</th>
                                    <th className="text-left py-2 px-3 font-medium text-slate-600">Method</th>
                                    <th className="text-left py-2 px-3 font-medium text-slate-600">Notes</th>
                                  </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-100">
                                  {getContractorPayments(contractor.id)
                                    .sort((a, b) => new Date(b.payment_date) - new Date(a.payment_date))
                                    .map((payment, idx) => (
                                      <tr key={payment.id || idx} className="hover:bg-slate-50">
                                        <td className="py-2 px-3 text-slate-700">
                                          {formatDate(payment.payment_date)}
                                        </td>
                                        <td className="py-2 px-3 font-medium text-green-600">
                                          ${parseFloat(payment.amount).toLocaleString()}
                                        </td>
                                        <td className="py-2 px-3">
                                          <Badge variant="outline" className="capitalize text-xs">
                                            {payment.payment_method?.replace('_', ' ') || 'N/A'}
                                          </Badge>
                                        </td>
                                        <td className="py-2 px-3 text-slate-500 max-w-[200px] truncate">
                                          {payment.notes || '-'}
                                        </td>
                                      </tr>
                                    ))}
                                </tbody>
                              </table>
                            </div>
                          )}
                          
                          {/* Quick Add Payment Button */}
                          <Button
                            size="sm"
                            variant="outline"
                            className="w-full mt-2"
                            onClick={() => {
                              setSelectedContractor(contractor);
                              setIsPaymentDialogOpen(true);
                            }}
                          >
                            <Plus className="w-4 h-4 mr-2" />
                            Add Payment
                          </Button>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>

      {/* Edit Contractor Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Edit Contractor</DialogTitle>
          </DialogHeader>
          {selectedContractor && (
            <form onSubmit={handleUpdateContractor} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Company Name *</Label>
                  <Input
                    required
                    value={selectedContractor.company || ''}
                    onChange={(e) => setSelectedContractor({...selectedContractor, company: e.target.value})}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Contact Name</Label>
                  <Input
                    value={selectedContractor.name || ''}
                    onChange={(e) => setSelectedContractor({...selectedContractor, name: e.target.value})}
                    placeholder="Optional"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Email *</Label>
                  <Input
                    type="email"
                    required
                    value={selectedContractor.email || ''}
                    onChange={(e) => setSelectedContractor({...selectedContractor, email: e.target.value})}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Phone</Label>
                  <Input
                    value={selectedContractor.phone || ''}
                    onChange={(e) => setSelectedContractor({...selectedContractor, phone: e.target.value})}
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Street Address</Label>
                <Input
                  value={selectedContractor.street_address || ''}
                  onChange={(e) => setSelectedContractor({...selectedContractor, street_address: e.target.value})}
                  placeholder="1811 Chandler St SW"
                />
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>City</Label>
                  <Input
                    value={selectedContractor.city || ''}
                    onChange={(e) => setSelectedContractor({...selectedContractor, city: e.target.value})}
                  />
                </div>
                <div className="space-y-2">
                  <Label>State</Label>
                  <Input
                    value={selectedContractor.state || ''}
                    onChange={(e) => setSelectedContractor({...selectedContractor, state: e.target.value})}
                    maxLength={2}
                  />
                </div>
                <div className="space-y-2">
                  <Label>ZIP Code</Label>
                  <Input
                    value={selectedContractor.zip || ''}
                    onChange={(e) => setSelectedContractor({...selectedContractor, zip: e.target.value})}
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Specialty</Label>
                  <Select
                    value={selectedContractor.specialty || ''}
                    onValueChange={(value) => setSelectedContractor({...selectedContractor, specialty: value})}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select specialty" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="general">General Contractor</SelectItem>
                      <SelectItem value="plumbing">Plumbing</SelectItem>
                      <SelectItem value="electrical">Electrical</SelectItem>
                      <SelectItem value="hvac">HVAC</SelectItem>
                      <SelectItem value="roofing">Roofing</SelectItem>
                      <SelectItem value="framing">Framing</SelectItem>
                      <SelectItem value="concrete">Concrete</SelectItem>
                      <SelectItem value="painting">Painting</SelectItem>
                      <SelectItem value="flooring">Flooring</SelectItem>
                      <SelectItem value="landscaping">Landscaping</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>SSN / EIN</Label>
                  <Input
                    value={selectedContractor.ein_ssn || ''}
                    onChange={(e) => setSelectedContractor({...selectedContractor, ein_ssn: e.target.value})}
                    placeholder="XXX-XX-XXXX or XX-XXXXXXX"
                  />
                </div>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setIsEditDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" className="bg-blue-600 hover:bg-blue-700">
                  Save Changes
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Add Payment Dialog */}
      <Dialog open={isPaymentDialogOpen} onOpenChange={setIsPaymentDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Record Payment</DialogTitle>
            <DialogDescription>
              Record a payment to {selectedContractor?.name}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleAddPayment} className="space-y-4">
            <div className="space-y-2">
              <Label>Amount *</Label>
              <Input
                required
                type="number"
                step="0.01"
                value={newPayment.amount}
                onChange={(e) => setNewPayment({...newPayment, amount: e.target.value})}
                placeholder="0.00"
              />
            </div>
            <div className="space-y-2">
              <Label>Payment Date</Label>
              <Input
                type="date"
                value={newPayment.payment_date}
                onChange={(e) => setNewPayment({...newPayment, payment_date: e.target.value})}
              />
            </div>
            <div className="space-y-2">
              <Label>Payment Method</Label>
              <Select
                value={newPayment.payment_method}
                onValueChange={(value) => setNewPayment({...newPayment, payment_method: value})}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="check">Check</SelectItem>
                  <SelectItem value="ach">ACH Transfer</SelectItem>
                  <SelectItem value="wire">Wire Transfer</SelectItem>
                  <SelectItem value="cash">Cash</SelectItem>
                  <SelectItem value="credit_card">Credit Card</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Notes</Label>
              <Input
                value={newPayment.notes}
                onChange={(e) => setNewPayment({...newPayment, notes: e.target.value})}
                placeholder="Payment for foundation work..."
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsPaymentDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" className="bg-blue-600 hover:bg-blue-700">
                Record Payment
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Generate 1099 Dialog */}
      <Dialog open={is1099DialogOpen} onOpenChange={setIs1099DialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Generate 1099-NEC</DialogTitle>
            <DialogDescription>
              Generate 1099-NEC form for {selectedContractor?.name}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Tax Year</Label>
              <Select value={selectedYear} onValueChange={setSelectedYear}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="2025">2025</SelectItem>
                  <SelectItem value="2024">2024</SelectItem>
                  <SelectItem value="2023">2023</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {selectedContractor && (
              <div className="bg-slate-50 p-4 rounded-lg">
                <p className="text-sm text-slate-600">Total payments in {selectedYear}:</p>
                <p className="text-2xl font-bold text-slate-800">
                  ${getContractorTotalPaid(selectedContractor.id, selectedYear).toLocaleString()}
                </p>
                {getContractorTotalPaid(selectedContractor.id, selectedYear) < 600 && (
                  <p className="text-xs text-amber-600 mt-2">
                    Note: 1099-NEC is only required for payments of $600 or more
                  </p>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setIs1099DialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={() => selectedContractor && generate1099(selectedContractor)}
              className="bg-blue-600 hover:bg-blue-700"
            >
              <Download className="w-4 h-4 mr-2" />
              Generate 1099
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ACH Payment Dialog */}
      <Dialog open={isACHPaymentDialogOpen} onOpenChange={setIsACHPaymentDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Landmark className="w-5 h-5 text-blue-600" />
              Send ACH Payment
            </DialogTitle>
            <DialogDescription>
              Send money directly to {selectedContractor?.name}'s bank account via ACH transfer.
              Funds typically arrive in 2-4 business days.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSendACHPayment} className="space-y-4">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                  <Users className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="font-medium text-slate-800">{selectedContractor?.name}</p>
                  <p className="text-sm text-slate-500">{selectedContractor?.company || selectedContractor?.email}</p>
                  <p className="text-xs text-green-600 flex items-center gap-1 mt-1">
                    <CheckCircle2 className="w-3 h-3" />
                    Bank account verified
                  </p>
                </div>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Payment Amount *</Label>
              <div className="relative">
                <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  required
                  type="number"
                  step="0.01"
                  min="1"
                  value={achPaymentAmount}
                  onChange={(e) => setAchPaymentAmount(e.target.value)}
                  placeholder="0.00"
                  className="pl-8 text-lg"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Description / Memo</Label>
              <Input
                value={achPaymentDescription}
                onChange={(e) => setAchPaymentDescription(e.target.value)}
                placeholder="Payment for foundation work..."
              />
            </div>
            
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800">
              <p className="font-medium">⏱️ ACH transfers take 2-4 business days</p>
              <p className="text-xs mt-1">The payment will be debited from your Stripe balance and deposited into the contractor's bank account.</p>
            </div>
            
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsACHPaymentDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" className="bg-blue-600 hover:bg-blue-700">
                <Send className="w-4 h-4 mr-2" />
                Send ${achPaymentAmount || '0.00'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Bank Setup Link Dialog */}
      <Dialog open={isBankSetupDialogOpen} onOpenChange={setIsBankSetupDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Landmark className="w-5 h-5 text-blue-600" />
              Bank Account Setup Link
            </DialogTitle>
            <DialogDescription>
              Share this secure link with {selectedContractor?.name} to set up their bank account for ACH payments.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="bg-slate-50 border rounded-lg p-4">
              <Label className="text-xs text-slate-500 uppercase tracking-wide">Secure Setup Link</Label>
              <div className="flex items-center gap-2 mt-2">
                <Input
                  readOnly
                  value={bankSetupUrl}
                  className="text-sm bg-white"
                />
                <Button 
                  type="button" 
                  variant="outline" 
                  size="icon"
                  onClick={copyBankSetupLink}
                >
                  <Copy className="w-4 h-4" />
                </Button>
              </div>
            </div>
            
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-3">
              <p className="font-medium text-slate-800">How it works:</p>
              <ol className="text-sm text-slate-600 space-y-2">
                <li className="flex items-start gap-2">
                  <span className="bg-blue-600 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs flex-shrink-0">1</span>
                  <span>Send this link to {selectedContractor?.name} via email or text</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="bg-blue-600 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs flex-shrink-0">2</span>
                  <span>They'll securely log into their bank through Stripe</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="bg-blue-600 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs flex-shrink-0">3</span>
                  <span>Once complete, you can send payments directly to their account</span>
                </li>
              </ol>
            </div>
            
            <div className="flex gap-2">
              <Button 
                className="flex-1 bg-blue-600 hover:bg-blue-700"
                onClick={() => window.open(bankSetupUrl, '_blank')}
              >
                <ExternalLink className="w-4 h-4 mr-2" />
                Open Link
              </Button>
              <Button 
                variant="outline"
                className="flex-1"
                onClick={() => {
                  const subject = encodeURIComponent('Set up your bank account for payments');
                  const body = encodeURIComponent(`Hi ${selectedContractor?.name},\n\nPlease use this secure link to set up your bank account so I can send you payments directly:\n\n${bankSetupUrl}\n\nThanks!`);
                  window.open(`mailto:${selectedContractor?.email}?subject=${subject}&body=${body}`);
                }}
              >
                <Mail className="w-4 h-4 mr-2" />
                Email Link
              </Button>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsBankSetupDialogOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* W9/COI Document Upload Dialog */}
      <Dialog open={isDocumentUploadOpen} onOpenChange={setIsDocumentUploadOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {documentType === 'w9' ? 'Upload W-9 Tax Form' : 'Upload Certificate of Insurance'}
            </DialogTitle>
            <DialogDescription>
              Upload {documentType === 'w9' ? 'W-9' : 'COI'} for {selectedContractor?.company || selectedContractor?.name || 'contractor'}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {documentType === 'coi' && (
              <div className="space-y-2">
                <Label>COI Expiration Date <span className="text-red-600">*</span></Label>
                <Input
                  type="date"
                  value={coiExpiration}
                  onChange={(e) => setCoiExpiration(e.target.value)}
                  min={new Date().toISOString().split('T')[0]}
                  required
                  data-testid="coi-expiration-input"
                />
                <p className="text-xs text-slate-500">Required — we use this to warn you before insurance lapses.</p>
              </div>
            )}
            
            <div className="space-y-2">
              <Label>Select File</Label>
              <Input
                type="file"
                accept=".pdf,.jpg,.jpeg,.png"
                onChange={(e) => setDocumentFile(e.target.files?.[0] || null)}
              />
              <p className="text-xs text-slate-500">Accepted formats: PDF, JPG, PNG</p>
            </div>
            
            {documentFile && (
              <div className="flex items-center gap-2 p-3 bg-slate-50 rounded-lg">
                <FileText className="w-5 h-5 text-blue-600" />
                <span className="text-sm text-slate-700">{documentFile.name}</span>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className="ml-auto"
                  onClick={() => setDocumentFile(null)}
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDocumentUploadOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleDocumentUpload}
              disabled={!documentFile || isUploadingDocument}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {isUploadingDocument ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Uploading...
                </>
              ) : (
                <>
                  <Upload className="w-4 h-4 mr-2" />
                  Upload {documentType === 'w9' ? 'W-9' : 'COI'}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default GlobalContractors;
